import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Search, X, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import GMark from "@/components/GMark";
import Seo from "@/components/Seo";
import FooterMeta, { TEAM_EMAIL } from "@/components/FooterMeta";
import { faqPage, breadcrumb } from "@/lib/schema";

// Flatten JSX answer nodes into a plain string so we can search inside them.
function jsxToText(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(jsxToText).join(" ");
  if (node.props && node.props.children) return jsxToText(node.props.children);
  return "";
}

const TOPICS = ["Opening a Gate", "My Gate or account", "Billing and payments", "Buying from a Gate", "Something else"];

function ContactForm({ prefill = "" }) {
  const [f, setF] = useState({ name: "", email: "", topic: TOPICS[0], message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const valid = f.name.trim() && /\S+@\S+\.\S+/.test(f.email) && f.message.trim().length >= 10;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    try {
      await api.post("/contact", { ...f, name: f.name.trim(), email: f.email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="rounded-3xl border border-[#DED5C3] bg-[#FFFCF6] p-8" data-testid="contact-success">
        <p className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E]">Message sent</p>
        <p className="font-editorial text-2xl text-[#20251F] mt-1">Thanks, {f.name.split(" ")[0]} — that's landed with the team.</p>
        <p className="text-sm text-stone-600 mt-2">We'll reply to <span className="font-mono-label text-[#3F674E]">{f.email}</span> as soon as we can.</p>
        <button type="button" onClick={() => { setSent(false); setF({ ...f, message: "" }); }} className="mt-4 text-sm text-[#3F674E] underline underline-offset-4" data-testid="contact-reset">
          Send another
        </button>
      </div>
    );
  }

  const field = "bg-white border-[#DED5C3] focus-visible:ring-[#3F674E]";
  return (
    <form onSubmit={submit} className="rounded-3xl border border-[#DED5C3] bg-[#FFFCF6] p-6 sm:p-8" data-testid="contact-form">
      <p className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E]">Get in touch</p>
      <h2 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] tracking-tight mt-1">Can't find it? Ask us.</h2>
      <p className="text-sm text-stone-600 mt-2">A real person reads every message. Prefer email? <a href={`mailto:${TEAM_EMAIL}`} className="underline text-[#3F674E]">{TEAM_EMAIL}</a></p>
      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <Input value={f.name} onChange={set("name")} placeholder="Your name" className={field} data-testid="contact-name" />
        <Input type="email" value={f.email} onChange={set("email")} placeholder="Your email" className={field} data-testid="contact-email" />
      </div>
      <select value={f.topic} onChange={set("topic")} className={`mt-3 w-full h-10 rounded-md border px-3 text-sm ${field}`} data-testid="contact-topic" aria-label="Topic">
        {TOPICS.map((t) => <option key={t}>{t}</option>)}
      </select>
      <Textarea value={f.message} onChange={set("message")} onFocus={() => { if (prefill && !f.message) setF((p) => ({ ...p, message: prefill })); }} rows={5} placeholder="How can we help?" className={`mt-3 resize-none text-[15px] ${field}`} data-testid="contact-message" />
      <input type="text" name="website" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
      <div className="mt-4 flex justify-end">
        <Button type="submit" disabled={busy || !valid} className="bg-[#20251F] hover:bg-black rounded-full text-white px-6" data-testid="contact-submit">
          {busy ? "Sending…" : "Send message"}
        </Button>
      </div>
    </form>
  );
}

const SECTIONS = [
  {
    title: "The basics",
    items: [
      {
        q: "What is Gate + Gather?",
        a: (
          <>
            <p>Gate + Gather is a simple selling platform for independent growers. Your Gate gives you one place to show what you have available, take orders, manage stock and tell customers how they can pay or collect.</p>
            <p className="mt-3 italic">You bring your customers. We make them easier to serve.</p>
          </>
        ),
      },
      {
        q: "Is Gate + Gather a marketplace?",
        a: (
          <>
            <p>No. Your Gate is your own seller storefront and your customers remain your customers.</p>
            <p className="mt-3">You can share your Gate anywhere you already sell or promote your plants — Instagram, Facebook, Facebook Marketplace, your existing website, email, markets, roadside signage or simply by sending someone the link.</p>
          </>
        ),
      },
      {
        q: "Do I need a website?",
        a: (
          <>
            <p>No.</p>
            <p className="mt-3">Your Gate gives you a simple online storefront with its own shareable address, for example:</p>
            <p className="mt-2 font-mono-label text-[#3F674E]">gateandgather.co.nz/g/petalpower</p>
            <p className="mt-3">If you already have a website, you can simply link your Gate from it.</p>
          </>
        ),
      },
      {
        q: "Can I use Gate + Gather if I already have a website?",
        a: (
          <>
            <p>Definitely.</p>
            <p className="mt-3">Your existing website can continue doing everything it already does. Simply add a button such as <em>Shop what's available</em> or <em>Shop our Gate</em> and link it directly to your Gate.</p>
            <p className="mt-3">You don't need to rebuild your website or add a full ecommerce system.</p>
          </>
        ),
      },
    ],
  },
  {
    title: "Bringing your customers",
    items: [
      {
        q: "I currently sell through Facebook or Instagram. Can I still use Gate + Gather?",
        a: (
          <>
            <p>Absolutely. You don't need to stop using the places where your customers already find you.</p>
            <p className="mt-3">Instead of managing orders through comments and messages, you can direct customers to your Gate to see what's currently available and place their order.</p>
            <p className="mt-3">For example, you might add your Gate to your Instagram bio, Facebook page, Marketplace listings or existing website.</p>
            <p className="mt-3 italic">Your audience stays where it is. Your Gate gives them somewhere simple to buy.</p>
          </>
        ),
      },
      {
        q: "How do I move my existing customers to my Gate?",
        a: (
          <>
            <p>We'll give you simple guides to help. You can:</p>
            <ul className="mt-3 space-y-1.5 list-disc pl-5">
              <li>add your Gate to your Instagram bio</li>
              <li>add or update the button on your Facebook page</li>
              <li>link it from your existing website</li>
              <li>include it in Facebook Marketplace listings</li>
              <li>share it in posts and stories</li>
              <li>send the link directly to existing customers</li>
              <li>add a QR code to your market table, nursery, roadside stall or signage</li>
            </ul>
            <p className="mt-3">You don't need to change everything at once. Your Gate can work alongside the way you already sell.</p>
          </>
        ),
      },
      {
        q: "Do customers need an account?",
        a: (
          <>
            <p>No.</p>
            <p className="mt-3">Customers can browse your available products and place an order without creating a Gate + Gather account. We want buying from a Gate to be as simple as possible.</p>
          </>
        ),
      },
      {
        q: "Can I sell at markets as well as through my Gate?",
        a: (
          <>
            <p>Yes. Gate + Gather is designed to work alongside the way you already sell.</p>
            <p className="mt-3">If you sell products in person, you can quickly adjust the quantity available in your Gate so your online stock remains accurate.</p>
            <p className="mt-3">You can also put a QR code on your market table so customers can find your Gate later.</p>
          </>
        ),
      },
    ],
  },
  {
    title: "Running your Gate",
    items: [
      {
        q: "How do I add something for sale?",
        a: (
          <>
            <p>Start with four things:</p>
            <p className="mt-3 font-serif-heading text-xl italic text-[#20251F]">Photo. Name. Price. Quantity.</p>
            <p className="mt-3">That's enough to list a product. You can add more information if you want to, but you shouldn't have to complete a complicated product form just to sell six kōwhai.</p>
          </>
        ),
      },
      {
        q: "Can I choose how customers pay?",
        a: (
          <>
            <p>Yes. When you set up your Gate, you choose which payment methods you want to accept.</p>
            <p className="mt-3">Depending on the options available to your Gate, this may include online payment, cash or payment on pickup. You can choose to accept online payments only, offline payments only, or offer customers a choice.</p>
            <p className="mt-3">Customers will only see the payment options you have enabled.</p>
          </>
        ),
      },
      {
        q: "What happens to my stock when someone orders?",
        a: (
          <>
            <p>Gate + Gather keeps your available stock up to date as orders are placed. If you have six plants available and a customer orders four, your Gate will show two remaining.</p>
            <p className="mt-3">For orders paid online, payment and stock management happen together. For orders being paid on pickup, the ordered stock is reserved so it isn't accidentally sold twice.</p>
          </>
        ),
      },
      {
        q: "Can customers pick up their order?",
        a: (
          <>
            <p>Yes. When you set up your Gate, you choose how customers can receive their orders. You may offer:</p>
            <ul className="mt-3 space-y-1.5 list-disc pl-5">
              <li>Pickup</li>
              <li>Local delivery or drop-off</li>
              <li>Both</li>
            </ul>
            <p className="mt-3">You set the relevant location, availability, instructions and any delivery charges. Customers see the options you've chosen when ordering.</p>
          </>
        ),
      },
      {
        q: "Do I have to show my home address publicly?",
        a: (
          <>
            <p>No. Your Gate can show your general area without publishing a private residential pickup address to everyone browsing.</p>
            <p className="mt-3">Detailed collection information can be provided to customers as part of the order and collection process.</p>
          </>
        ),
      },
      {
        q: "What happens when something sells out?",
        a: (
          <>
            <p>A sold-out product doesn't necessarily disappear. Where enabled, customers can choose <em>Notify me when available</em>.</p>
            <p className="mt-3">That gives you useful information about what people are waiting for and gives interested customers a way to hear when you have more available — without repeatedly messaging you.</p>
          </>
        ),
      },
      {
        q: "What if I only sell during part of the year?",
        a: (
          <>
            <p>That's fine. You can <em>Close my Gate for now</em> when you're not selling and reopen it when you're ready.</p>
            <p className="mt-3">You shouldn't need to rebuild your Gate every season.</p>
          </>
        ),
      },
    ],
  },
  {
    title: "Pricing and ownership",
    items: [
      {
        q: "Does Gate + Gather take a percentage of my sales?",
        a: (
          <>
            <p>No. Gate + Gather is designed around a subscription rather than taking a commission from every sale.</p>
            <p className="mt-3">If you use online card payments, normal payment-processing fees may still apply. Full pricing and payment-processing costs will always be shown clearly.</p>
          </>
        ),
      },
      {
        q: "How many products can I sell?",
        a: (
          <>
            <p>Gate + Gather is designed for independent sellers with relatively small, changing inventories rather than enormous ecommerce catalogues.</p>
            <p className="mt-3">Different plans will support different numbers of active products, so you can choose the level that suits the size of your Gate. Full plan limits and pricing will be available before you subscribe.</p>
          </>
        ),
      },
      {
        q: "Who is responsible for the products sold through a Gate?",
        a: (
          <>
            <p>Each Gate is operated by its independent seller.</p>
            <p className="mt-3">Gate + Gather provides the technology that allows sellers to display products, receive orders and manage sales. The seller is responsible for the products they list and sell, including their descriptions, pricing, availability, fulfilment and applicable legal or regulatory requirements.</p>
            <p className="mt-3">Full details will be contained in the Gate + Gather Terms of Service.</p>
          </>
        ),
      },
      {
        q: "Is my Gate mine?",
        a: (
          <>
            <p>Your Gate represents your business, your products and your customers. Gate + Gather provides the selling platform underneath it. Your business name, photography and products remain front and centre.</p>
            <p className="mt-4 font-serif-heading text-xl italic text-[#20251F]">Your Gate. Your customers. Not a marketplace.</p>
          </>
        ),
      },
    ],
  },
];

export default function FAQ() {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return SECTIONS.map((s) => ({ ...s, items: s.items }));
    return SECTIONS
      .map((s) => ({
        ...s,
        items: s.items.filter((it) =>
          (it.q + " " + jsxToText(it.a)).toLowerCase().includes(q)
        ),
      }))
      .filter((s) => s.items.length > 0);
  }, [q]);
  const totalMatches = filtered.reduce((n, s) => n + s.items.length, 0);
  const isSearching = q.length > 0;

  return (
    <div className="min-h-screen bg-[#F5F0E5] text-[#20251F]">
      <Seo
        title="Frequently asked questions"
        description="Common questions about Gate + Gather — how a Gate works, how customers order, how payment and pickup happen, and how it fits alongside markets, Facebook and Instagram."
        path="/faq"
        jsonLd={[
          faqPage(
            SECTIONS.flatMap((s) =>
              s.items.map((it) => ({ question: it.q, answer: jsxToText(it.a) }))
            )
          ),
          breadcrumb([
            { name: "Gate + Gather", path: "/" },
            { name: "FAQ", path: "/faq" },
          ]),
        ]}
      />
      <header className="border-b border-[#DED5C3] bg-[#F5F0E5]/85 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="faq-nav-brand">
            <GMark size={30} className="rounded-lg shrink-0" />
            <span className="font-serif-heading text-xl tracking-tight">Gate<span className="brand-plus text-[1.05em]"> + </span>Gather</span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link to="/pricing" data-testid="faq-nav-pricing" className="text-sm text-[#6B6E67] hover:text-[#20251F] px-3 py-2 hidden sm:inline">Pricing</Link>
            <Link to="/for-growers" data-testid="faq-nav-growers" className="text-sm text-[#6B6E67] hover:text-[#20251F] px-3 py-2 hidden sm:inline">For growers</Link>
            <Link to="/seller/login" data-testid="faq-nav-login" className="text-sm text-[#6B6E67] hover:text-[#20251F] px-2 sm:px-3 py-2 whitespace-nowrap">Log in</Link>
            <Link to="/seller/signup" data-testid="faq-nav-signup">
              <Button className="bg-[#20251F] hover:bg-black rounded-full text-white h-9 sm:h-10 px-3 sm:px-4 text-sm whitespace-nowrap">Open your Gate</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-24">
        <p className="font-mono-label text-xs uppercase tracking-widest text-[#3F674E]/80">Common questions</p>
        <h1 className="font-serif-heading text-4xl sm:text-6xl leading-[1.02] tracking-tight text-[#20251F] mt-3">
          Frequently <em className="text-[#94AE29]">asked</em>.
        </h1>
        <p className="mt-6 text-base sm:text-lg text-stone-700 max-w-2xl leading-relaxed">
          A calm walk-through of what Gate + Gather is, how it fits alongside the places you already sell, and how each part of your Gate quietly works underneath.
        </p>

        <div className="mt-10 relative" data-testid="faq-search-wrap">
          <Search className="w-4 h-4 text-[#6B6E67] absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            data-testid="faq-search-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search — try pickup, payment, waitlist, plants…"
            className="pl-10 pr-11 h-12 rounded-full bg-white border-[#DED5C3] focus-visible:ring-[#3F674E] text-[15px]"
            aria-label="Search FAQ"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="faq-search-clear"
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-[#6B6E67] hover:text-[#20251F] hover:bg-[#F5F0E5]"
            >
              <X className="w-4 h-4" />
            </button>
          )}
          {isSearching && (
            <p className="mt-3 font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#6B6E67]" data-testid="faq-search-summary">
              {totalMatches === 0 ? "No matches" : totalMatches === 1 ? "1 match" : `${totalMatches} matches`}
              {totalMatches > 0 ? " · tap to expand" : ""}
            </p>
          )}
        </div>

        <a href="#get-in-touch" data-testid="ask-us-open" className="mt-3 inline-flex items-center gap-2 text-sm text-[#3F674E] hover:text-[#20251F] underline underline-offset-4 decoration-[#DED5C3] hover:decoration-[#20251F] transition-colors">
          <HelpCircle className="w-4 h-4" /> Can't find it? Get in touch
        </a>

        {filtered.length === 0 ? (
          <div className="mt-14 rounded-2xl border border-dashed border-[#DED5C3] bg-[#FFFCF6] p-10 text-center" data-testid="faq-empty">
            <p className="font-editorial text-xl text-[#20251F]">Nothing matches "{query}" — yet.</p>
            <p className="text-sm text-stone-600 mt-2">Try a plainer word like <em>pickup</em>, <em>stock</em> or <em>waitlist</em>. If it's still missing, we probably owe you an answer — <Link to="/seller/signup" className="underline text-[#3F674E]">open a Gate</Link> and ask us anyway.</p>
          </div>
        ) : (
          <div className="mt-14 space-y-14">
            {filtered.map((section, si) => (
              <section key={section.title} data-testid={`faq-section-${si}`}>
                <p className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E]">{String(si + 1).padStart(2, "0")} · Section</p>
                <h2 className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] tracking-tight mt-2 mb-4">{section.title}</h2>
                <Accordion type="single" collapsible className="border-t border-[#DED5C3]" defaultValue={isSearching ? `s${si}-i0` : undefined}>
                  {section.items.map((item, i) => (
                    <AccordionItem
                      key={item.q}
                      value={`s${si}-i${i}`}
                      data-testid={`faq-item-${si}-${i}`}
                      className="border-b border-[#DED5C3]"
                    >
                      <AccordionTrigger className="text-left font-editorial text-lg sm:text-xl text-[#20251F] hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[15px] leading-relaxed text-stone-700 pb-6 pr-2">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            ))}
          </div>
        )}

        <section id="get-in-touch" className="mt-20 scroll-mt-24">
          <ContactForm prefill={query} />
        </section>

        <section className="mt-10 rounded-3xl border border-[#DED5C3] bg-[#FFFCF6] p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
          <GMark size={56} className="rounded-xl shrink-0" />
          <div className="flex-1">
            <p className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E]">Still curious?</p>
            <p className="font-serif-heading text-2xl sm:text-3xl text-[#20251F] tracking-tight mt-1">Open your Gate — no obligation.</p>
            <p className="text-sm text-stone-600 mt-2">List a plant before you sign up. Auth only happens at the end.</p>
          </div>
          <Link to="/seller/signup" data-testid="faq-cta-open">
            <Button size="lg" className="bg-[#20251F] hover:bg-black rounded-full h-12 px-6 text-white">
              Open your Gate <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </section>
      </main>

      <footer className="border-t border-[#DED5C3] py-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 flex-wrap">
          <p className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#6B6E67]">Simple selling for independent growers</p>
          <div className="flex items-center gap-4 flex-wrap">
            <Link to="/policy/seller-policy" data-testid="footer-seller-policy-link" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Seller Policy</Link>
            <Link to="/policy/what-can-be-sold" data-testid="footer-what-sold-link" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">What can be sold</Link>
            <Link to="/policy/privacy" data-testid="footer-privacy-link" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Privacy</Link>
            <Link to="/policy/terms" data-testid="footer-terms-link" className="font-mono-label text-[11px] uppercase tracking-[0.14em] text-[#3F674E] hover:text-[#20251F]">Terms</Link>
            <Link to="/" className="flex items-center gap-2 group">
              <GMark size={28} className="rounded-md shrink-0" />
              <span className="font-serif-heading text-base">Gate<span className="brand-plus"> + </span>Gather</span>
            </Link>
          </div>
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-4">
          <FooterMeta />
        </div>
      </footer>
    </div>
  );
}
